import React, { useEffect, useState, useCallback } from 'react';
import { useDemoTour } from './DemoTourContext';
import { Button } from '../ui/button';
import { X, ArrowRight, Sparkles } from 'lucide-react';

function getTargetRect(testId) {
  if (!testId) return null;
  const el = document.querySelector(`[data-testid="${testId}"]`);
  if (!el) return null;
  return el.getBoundingClientRect();
}

export function DemoTourOverlay() {
  const tour = useDemoTour();
  const [rect, setRect] = useState(null);

  const recalc = useCallback(() => {
    if (!tour?.active || tour.phase !== 'prompt' || !tour.step?.navTestId) {
      setRect(null);
      return;
    }
    setRect(getTargetRect(tour.step.navTestId));
  }, [tour?.active, tour?.phase, tour?.step]);

  useEffect(() => {
    recalc();
    const id = setInterval(recalc, 400);
    window.addEventListener('resize', recalc);
    return () => {
      clearInterval(id);
      window.removeEventListener('resize', recalc);
    };
  }, [recalc]);

  // The highlighted nav item can be scrolled out of view (long sidebar, short
  // viewport) — auto-scroll it into view so the spotlight is never off-screen.
  useEffect(() => {
    if (!tour?.active || tour.phase !== 'prompt' || !tour.step?.navTestId) return;
    const testId = tour.step.navTestId;
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-testid="${testId}"]`);
      if (!el) return;
      const r = el.getBoundingClientRect();
      const fullyVisible = r.top >= 0 && r.bottom <= window.innerHeight;
      if (!fullyVisible) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [tour?.active, tour?.phase, tour?.step]);

  if (!tour?.active || !tour.step) return null;

  const isPrompt = tour.phase === 'prompt' && rect;
  const isFinal = tour.stepIndex === tour.totalSteps - 1;

  return (
    <div
      className="fixed inset-0 z-[9999]"
      style={{ pointerEvents: isPrompt ? 'none' : 'auto' }}
      data-testid="demo-tour-overlay"
    >
      {/* Dimmed backdrop with a spotlight cutout around the target nav item */}
      <div
        className="absolute inset-0 transition-all duration-300"
        style={{
          background: isPrompt
            ? `radial-gradient(circle at ${rect.left + rect.width / 2}px ${rect.top + rect.height / 2}px, transparent ${Math.max(rect.width, rect.height) / 1.3 + 14}px, rgba(15,23,42,0.72) ${Math.max(rect.width, rect.height) / 1.3 + 60}px)`
            : 'rgba(15,23,42,0.72)',
          pointerEvents: isPrompt ? 'none' : 'auto',
        }}
      />

      {isPrompt && (
        <>
          <div
            className="absolute rounded-lg ring-4 ring-emerald-400 animate-pulse pointer-events-none"
            style={{ left: rect.left - 4, top: rect.top - 4, width: rect.width + 8, height: rect.height + 8 }}
          />
          <div
            className="absolute bg-slate-900 text-white rounded-xl shadow-2xl p-4 w-72 pointer-events-auto"
            style={{ left: Math.min(rect.right + 16, window.innerWidth - 300), top: Math.max(rect.top - 8, 16) }}
            data-testid="demo-tour-prompt"
          >
            <div className="flex items-center gap-2 text-emerald-400 text-xs font-semibold uppercase tracking-wide mb-1">
              <Sparkles className="h-3.5 w-3.5" /> Click here
            </div>
            <p className="text-sm">Click the highlighted tab to continue the tour.</p>
            <button onClick={tour.skip} className="text-xs text-slate-400 hover:text-white mt-3" data-testid="demo-tour-skip-prompt">
              Skip tour
            </button>
          </div>
        </>
      )}

      {!isPrompt && (
        <div className="absolute inset-0 flex items-center justify-center p-4 pointer-events-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative animate-in fade-in zoom-in-95 duration-200" data-testid="demo-tour-card">
            <button onClick={tour.skip} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground" data-testid="demo-tour-close-btn">
              <X className="h-4 w-4" />
            </button>
            <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-1">
              Step {tour.stepIndex + 1} of {tour.totalSteps}
            </p>
            <h3 className="text-lg font-bold mb-2" data-testid="demo-tour-title">{tour.step.title}</h3>
            <p className="text-sm text-muted-foreground mb-5" data-testid="demo-tour-body">{tour.step.body}</p>
            <div className="flex items-center justify-between">
              <button onClick={tour.skip} className="text-xs text-muted-foreground hover:text-foreground" data-testid="demo-tour-skip-btn">
                Skip tour
              </button>
              {isFinal ? (
                <Button onClick={tour.finish} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="demo-tour-finish-btn">
                  Get Started for Real <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              ) : (
                <Button onClick={tour.next} data-testid="demo-tour-next-btn">
                  Next <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
